import { $, $$, pageData } from '../lib/dom.js';
import { api } from '../lib/api.js';
import { toast } from '../lib/toast.js';
import { ct } from '../lib/i18n.js';

/**
 * The gift page.
 *
 * One click, one redirection to Stripe. No form and no save bar: nothing here
 * is a setting, and the only state that changes is at the other end.
 */

export function initGiftPage(): void {
  const claim = $<HTMLButtonElement>('[data-claim-votes]');
  const referral = $<HTMLButtonElement>('[data-claim-referral]');
  const plans = $$<HTMLElement>('[data-gift-plan]');

  // The claim buttons stand alone when the server already pays: the plans are
  // hidden then, and returning early would take the buttons with them.
  if (plans.length === 0 && !claim && !referral) return;

  const { guildId } = pageData<{ guildId: string }>();

  // Votes and referral milestones: one button, one route, the same answer.
  const claims: [HTMLButtonElement | null, string][] = [
    [claim, '/api/billing/vote-days'],
    [referral, '/api/billing/referral-days'],
  ];

  for (const [button, route] of claims) {
    if (!button) continue;

    button.addEventListener('click', () => {
      void (async () => {
        button.setAttribute('disabled', 'true');

        try {
          const answer = await api.post<{ days: number }>(route, { guildId });

          toast(ct('votesClaimed', 'Days added.') + ` (${String(answer.days)})`, 'success');
          // Reloaded rather than patched: the page shows the subscription's end
          // date and the votes left, and both have just moved.
          window.location.reload();
        } catch (error) {
          button.removeAttribute('disabled');
          toast(
            error instanceof Error
              ? error.message
              : ct('unexpectedError', 'An unexpected error occurred.'),
            'error',
          );
        }
      })();
    });
  }

  if (plans.length === 0) return;

  for (const button of plans) {
    button.addEventListener('click', () => {
      void (async () => {
        /*
         * Every button disabled, not just the one pressed.
         *
         * A second click on a different plan while the first session is being
         * created is a second Stripe session, and the person ends up on
         * whichever page loads last — having agreed to the other one.
         */
        for (const other of plans) other.setAttribute('disabled', 'true');

        try {
          const answer = await api.post<{ url: string }>('/api/billing/gift', {
            guildId,
            plan: button.dataset.giftPlan ?? 'monthly',
          });

          window.location.href = answer.url;
        } catch (error) {
          for (const other of plans) other.removeAttribute('disabled');
          toast(
            error instanceof Error
              ? error.message
              : ct('unexpectedError', 'An unexpected error occurred.'),
            'error',
          );
        }
      })();
    });
  }
}
